import { createElement, useMemo, type MouseEvent, type ReactNode } from 'react';
import type { ThemeConfig } from 'antd';
import type { Locale } from 'antd/es/locale';
import enGB from 'antd/locale/en_GB';
import plPL from 'antd/locale/pl_PL';
import ukUA from 'antd/locale/uk_UA';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/pl';
import 'dayjs/locale/uk';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'context/ThemeContext';
import { getLocale } from 'utils/helpers/functions/getLocale';

export type Dayjs = dayjs.Dayjs;

export const toDateValue = (isoDate: string): Dayjs | null =>
    isoDate ? dayjs(isoDate) : null;

export const toTimeValue = (time: string): Dayjs | null =>
    /^\d{2}:\d{2}$/.test(time) ? dayjs(`2000-01-01T${time}:00`) : null;

export const dateFormat = { format: 'DD.MM.YYYY', type: 'mask' as const };

/** Вище offcanvas задачі (1085) і модалки, інакше панель відкривається під ними. */
export const PICKER_Z_INDEX = 1200;

export const PICKER_CLASS_NAME = 'app-date-picker';

const keepPickerFocus = (panel: ReactNode) =>
    createElement(
        'div',
        {
            onMouseDown: (event: MouseEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
            },
            onClick: (event: MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
            },
        },
        panel,
    );

export const pickerPopupProps = {
    classNames: {
        popup: {
            root: 'app-date-picker-popup',
        },
    },
    getPopupContainer: () => document.body,
    styles: {
        popup: {
            root: { zIndex: PICKER_Z_INDEX },
        },
    },
    panelRender: keepPickerFocus,
};

const resolveDayjsLocale = (language: string): string => {
    const code = getLocale(language);
    if (code.startsWith('pl')) return 'pl';
    if (code.startsWith('en')) return 'en-gb';
    return 'uk';
};

const resolveAntdLocale = (language: string): Locale => {
    const code = getLocale(language);
    if (code.startsWith('pl')) return plPL;
    if (code.startsWith('en')) return enGB;
    return ukUA;
};

const readCssColor = (name: string, fallback: string): string => {
    if (typeof document === 'undefined') return fallback;
    return getComputedStyle(document.body).getPropertyValue(name).trim() || fallback;
};

const buildPickerTheme = (dark: boolean): ThemeConfig => {
    const primary = readCssColor('--bs-primary', '#4680ff');
    const primaryRgb = readCssColor('--bs-primary-rgb', '70, 128, 255');

    return {
        token: {
            colorPrimary: primary,
            borderRadius: 6,
            fontSize: 14,
            controlHeight: 36,
            fontFamily: 'inherit',
            colorBgContainer: dark ? '#263240' : '#ffffff',
            colorBgElevated: dark ? '#131920' : '#ffffff',
            colorBorder: dark ? '#303f50' : '#bec8d0',
            colorSplit: dark ? '#303f50' : '#e7eaee',
            colorText: dark ? '#bfbfbf' : '#131920',
            colorTextHeading: dark ? '#fafafa' : '#131920',
            colorTextPlaceholder: dark ? '#6b7280' : '#9ca3af',
            colorTextDisabled: dark ? '#bfbfbf' : '#131920',
            colorBgContainerDisabled: dark ? '#263240' : '#f3f5f7',
            colorBorderDisabled: dark ? '#303f50' : '#bec8d0',
            colorTextQuaternary: primary,
            colorIcon: dark ? '#c5ced6' : '#5b6b79',
            colorIconHover: primary,
            controlItemBgActive: primary,
            controlItemBgActiveHover: primary,
            controlOutline: `rgba(${primaryRgb}, 0.25)`,
            controlOutlineWidth: 3,
            boxShadowSecondary: dark
                ? '0 8px 28px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.25)'
                : '0 8px 24px rgba(27, 46, 94, 0.12)',
        },
        components: {
            DatePicker: {
                zIndexPopup: PICKER_Z_INDEX,
                activeBorderColor: primary,
                hoverBorderColor: dark ? '#6b7280' : '#9ca3af',
                cellHoverBg: dark ? '#19212a' : '#f3f5f7',
            },
        },
    };
};

export const useDateTimePicker = () => {
    const { i18n } = useTranslation();
    const { darkTheme } = useTheme();
    const locale = resolveAntdLocale(i18n.language);
    dayjs.locale(resolveDayjsLocale(i18n.language));

    const theme = useMemo(() => buildPickerTheme(Boolean(darkTheme)), [darkTheme]);

    return { locale, theme };
};

export const isDateOutsideRange = (date: Dayjs, min?: string, max?: string): boolean => {
    if (min && date.isBefore(dayjs(min), 'day')) return true;
    if (max && date.isAfter(dayjs(max), 'day')) return true;
    return false;
};
